import type { QueryClient } from '@tanstack/react-query';
import { TRPCClientError } from '@trpc/client';
import type { RouterAppContext } from '@/routes/__root';

export type EnsureQueryDataOptions = Parameters<QueryClient['ensureQueryData']>[0];

export interface WaitForClerkSessionOptions {
  timeoutMs?: number;
  pollIntervalMs?: number;
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isAuthenticationPendingError(error: unknown) {
  if (error instanceof TRPCClientError) {
    const code = error.data?.code;
    if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN') {
      return true;
    }
    const message = error.message?.toLowerCase() ?? '';
    return message.includes('unauthorized') || message.includes('authentication');
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return message.includes('unauthorized') || message.includes('authentication');
  }

  return false;
}

export async function waitForClerkSession(options: WaitForClerkSessionOptions = {}): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false;
  }

  const timeoutMs = options.timeoutMs ?? 4000;
  const pollIntervalMs = options.pollIntervalMs ?? 100;
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    const clerk = (window as unknown as { Clerk?: any }).Clerk;

    if (clerk?.session?.id) {
      return true;
    }

    if (clerk?.user?.id) {
      return true;
    }

    if (clerk?.loaded === true || clerk?.isLoaded === true) {
      if (clerk?.session?.status === 'active') {
        return true;
      }
    }

    await delay(pollIntervalMs);
  }

  return false;
}

export async function safeEnsureQueryData<TData>(
  queryClient: QueryClient,
  options: EnsureQueryDataOptions,
): Promise<TData | undefined> {
  try {
    return (await queryClient.ensureQueryData(options)) as TData;
  } catch (error) {
    if (isAuthenticationPendingError(error)) {
      console.warn('[safeEnsureQueryData] Auth not ready, skipping prefetch', error);
      return undefined;
    }
    throw error;
  }
}

type EnsureQueryDataFn = <TData>(options: EnsureQueryDataOptions) => Promise<TData | undefined>;

export interface LoadAuthGuardArgs<TParams = Record<string, unknown>> {
  params: TParams;
  context: RouterAppContext;
  ensureQueryData: EnsureQueryDataFn;
  sessionReady: boolean;
}

export function loadAuthGuard<TParams = Record<string, unknown>, TReturn = unknown>(
  loader: (args: LoadAuthGuardArgs<TParams>) => Promise<TReturn>,
  options?: WaitForClerkSessionOptions,
) {
  return async ({
    params,
    context,
  }: {
    params: TParams;
    context: RouterAppContext;
  }) => {
    const sessionReady =
      typeof window === 'undefined' ? false : await waitForClerkSession(options);

    const ensureQueryData: EnsureQueryDataFn = async (ensureOptions) => {
      if (!sessionReady) {
        return undefined;
      }
      return safeEnsureQueryData(context.queryClient, ensureOptions);
    };

    return loader({
      params,
      context,
      ensureQueryData,
      sessionReady,
    });
  };
}
